import type { ReactNode } from "react";
import { Link, Outlet } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { t } from "../i18n/t";
import { routes } from "../lib/routes";
import { AttributionProvider } from "./Attribution";
import { Footer } from "./Footer";
import { Header } from "./Header";
import { RequireAuth } from "./RequireAuth";

function SkipLink() {
  return (
    <a className="skip-link" href="#main">
      {t("app.skipToContent")}
    </a>
  );
}

/**
 * Signed-in app shell: header with switcher and nav, main content, footer with attribution.
 * Used as a layout route (renders the matched child) or around explicit `children`.
 */
export function AppLayout({ children }: { children?: ReactNode }) {
  return (
    <RequireAuth>
      <AttributionProvider>
        <SkipLink />
        <Header />
        <main id="main" className="app-main" tabIndex={-1}>
          {children ?? <Outlet />}
        </main>
        <Footer />
      </AttributionProvider>
    </RequireAuth>
  );
}

/** Shell for pages that need no login (auth flows and information pages). */
export function PublicLayout() {
  return (
    <AttributionProvider>
      <SkipLink />
      <header className="app-header public-header">
        <div className="header-bar">
          <Link to={routes.home()} className="brand">
            {t("app.name")}
          </Link>
        </div>
      </header>
      <main id="main" className="app-main public-main" tabIndex={-1}>
        <Outlet />
      </main>
      <Footer />
    </AttributionProvider>
  );
}

/** Shell for the public sales site: brand, section links, login (or "go to the app"), footer. */
export function MarketingLayout({ children }: { children?: ReactNode }) {
  const { status } = useAuth();
  const signedIn = status === "authenticated";
  return (
    <AttributionProvider>
      <SkipLink />
      <header className="app-header public-header marketing-header">
        <div className="header-bar">
          <Link to={routes.home()} className="brand">
            {t("app.name")}
          </Link>
          <nav aria-label={t("marketing.nav")}>
            <ul className="nav-list">
              <li>
                <Link to={routes.kommuner()}>{t("marketing.seeKommune")}</Link>
              </li>
              {!signedIn && (
                <>
                  <li>
                    <Link to={`${routes.home()}#slik`}>{t("marketing.how")}</Link>
                  </li>
                  <li>
                    <Link to={`${routes.home()}#priser`}>{t("marketing.prices")}</Link>
                  </li>
                </>
              )}
              <li>
                <Link
                  to={signedIn ? routes.home() : routes.login()}
                  className="btn btn-small nav-cta"
                >
                  {signedIn ? t("marketing.toApp") : t("marketing.login")}
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      </header>
      <main id="main" className="app-main public-main" tabIndex={-1}>
        {children ?? <Outlet />}
      </main>
      <Footer />
    </AttributionProvider>
  );
}
